import React, { useState } from 'react'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Button } from 'blaze-ui/Button'
import Styles from './ConnectedIntegrationsButton.module.scss'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Menu } from 'components/Menu/Menu'
import { Item, Section } from '@react-stately/collections'
import { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import { extractProfileInfo } from 'components/ExternalIntegrationSelect/utils'
import Avatar from 'common/Avatar/Avatar'
import { getInitials } from 'utils'
import { Text } from 'blaze-ui/Text/Text'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import UserProfileAdd2 from 'blaze-ui/icons/20/UserProfileAdd2'
import { ChevronDown2, Trash2 } from 'blaze-ui/icons'
import Silhouette from 'components/Previews/InstagramPreview/silhouette.jpg'
import { useInitiateOauthTrigger } from 'hooks/useInitiateOauthTrigger/useInitiateOauthTrigger'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useConnectedAccountCardProps } from 'components/IntegrationsModal/ManageIntegrationAccounts/hooks'

interface Props extends Omit<ButtonProps, 'frontIcon' | 'endIcon'> {
  integrations: ExternalIntegration[]
}

const ConnectedIntegrationsButtonIcon = ({ integrations }: Props) => {
  if (integrations.length === 0) {
    return null
  }

  // Calculate width: 20px (avatar size) + 8px * (number of avatars - 1) for overlapping
  const stackWidth = 20 + 8 * (integrations.length - 1)

  return (
    <div className={Styles.avatarStack} style={{ width: `${stackWidth}px` }}>
      {integrations.map((integration, index) => {
        const { avatar, name } = extractProfileInfo(integration)
        return (
          <div
            key={integration.id}
            className={Styles.stackedAvatar}
            style={{ left: `${index * 8}px`, zIndex: integrations.length - index }}
          >
            <Avatar size={20} label={getInitials(name)} url={avatar || Silhouette} />
          </div>
        )
      })}
    </div>
  )
}

const ConnectedIntegrationItem = ({ integration }: { integration: ExternalIntegration }) => {
  const { avatar, name } = extractProfileInfo(integration)
  const { onDisconnectAccount } = useConnectedAccountCardProps(integration)

  return (
    <div className={Styles.menuItem}>
      <div className={Styles.menuItemAvatar}>
        <Avatar size={28} label={getInitials(name)} url={avatar || Silhouette} />
      </div>
      <div className={Styles.menuItemContent}>
        <Text variant="secondary" color="var(--dark-80)">
          {name}
        </Text>
        <Text variant="metadata" color="var(--dark-40)">
          ID: {integration.provider_id}
        </Text>
      </div>
      <IconButton variant="tertiary" size="sm" icon={Trash2} onPress={onDisconnectAccount} />
    </div>
  )
}

export const ConnectedIntegrationsButton = ({
  integrations = [],
  variant = 'tertiary',
  size = 'md',
  ...props
}: Props): React.ReactElement => {
  const firstIntegration = integrations[0]
  const provider = firstIntegration?.provider
  const { connect } = useConnectAccountPopup({ provider })
  const buttonText = integrations.length === 1 ? firstIntegration.name : `${integrations.length} Accounts`
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const { handleConnect } = useInitiateOauthTrigger({
    provider,
    popUp: true,
    onInitiatedOauth: (popup?: Window) => {
      connect(popup, { isAdditionalAccount: true })
    },
  })

  const handleMenuAction = (key: React.Key) => {
    if (key === 'connect-extra' && provider) {
      setIsMenuOpen(false)
      handleConnect()
    }
  }

  return (
    <MenuTrigger popoverClassName={Styles.popover} isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <Button
        {...props}
        variant={variant}
        size={size}
        aria-label={`${integrations.length} Connected Integrations`}
        frontIcon={() => <ConnectedIntegrationsButtonIcon integrations={integrations} />}
        endIcon={ChevronDown2}
      >
        {buttonText}
      </Button>
      <div>
        <div className={Styles.connectedIntegrations}>
          {integrations.map((integration) => {
            return <ConnectedIntegrationItem key={integration.id} integration={integration} />
          })}
        </div>
        <Menu aria-label="Connected Integrations" onAction={handleMenuAction}>
          <Section>
            <Item key="connect-extra" textValue="Connect Extra Account">
              <div className={Styles.connectExtraItem}>
                <div className={Styles.addIconContainer}>
                  <UserProfileAdd2 size={15} />
                </div>
                <div className={Styles.menuItemContent}>
                  <Text variant="secondary" color="var(--dark-80)">
                    Connect Extra Account
                  </Text>
                  <Text variant="metadata" color="var(--dark-40)">
                    $10/month
                  </Text>
                </div>
              </div>
            </Item>
          </Section>
        </Menu>
      </div>
    </MenuTrigger>
  )
}
